<script setup lang="ts">
import { provideDateTimeZone, TrellisAnnouncer, TrellisToastProvider } from '@akaal/trellis-ui'
import { onErrorCaptured, ref } from 'vue'
import { RouterView, useRouter } from 'vue-router'

import { ApiError } from '@/api/errors'
import { leaveTo } from '@/auth/browser'
import { useSettingsStore } from '@/stores/settings'
import ErrorView from '@/views/ErrorView.vue'

const settings = useSettingsStore()
const router = useRouter()

// Every date below shows its timestamp in this zone (MRS 9).
provideDateTimeZone(() => settings.timezone)

// The error boundary of the application. A page that throws is replaced by
// the error view; the error goes on to the handler of GlitchTip. A navigation
// to another page clears it. A refusal of the API is not a broken page: the
// screen that sent the call reports it, so the page stays.
const failed = ref(false)

onErrorCaptured((error) => {
  failed.value = !(error instanceof ApiError)
})

router.afterEach(() => {
  failed.value = false
})

/** A full load is the only way back to a known state after a page threw. */
function reload() {
  leaveTo(window.location.href)
}
</script>

<template>
  <TrellisToastProvider />
  <TrellisAnnouncer />
  <ErrorView v-if="failed" @retry="reload" />
  <RouterView v-else />
</template>

<style>
/* The global rules of the application: the shells and the sign-in pages
   reach the edge of the window, and the brand colours of the mark (fixed in
   both themes: they are the logo, not the accent). Every other style is
   scoped or a token. */
:root {
  --brand-mark: #2563eb;
  --brand-mark-ground: #ffffff;
}

body {
  margin: 0;
  color: var(--trellis-color-ink);
  font-family: var(--trellis-font-body);
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
  }
}
</style>
